// Analie ElasticSearch

import "whatwg-fetch";
import { intersection, sortBy, pick, omit } from "lodash";
import { all } from "awaity/esm";
import { optionsGen } from "../helpers/api";
import { toESQuery, toESDateHistogramQuery, toESDateHistogramGroupByQuery, toESGroupByQuery, toESDoubleGroupByQuery, toESTripleGroupByQuery, nestedPathAggs } from "../helpers/query";
import { toTitleCase } from "../helpers/converters";
import { API_URL } from "../config";

export const ES_URL = `${API_URL}/cases/ptab/_search`;

export function getCaseDetail(caseNumber) {
  return fetch(`${API_URL}/cases/ptab/${caseNumber}`, optionsGen("GET", false, false)).then(resp => resp.json());
}

export function esqCases(queries, highlight = false) {
  return toESQuery({ sort: "-filing_date", ...queries }, ["filings"], 20, highlight);
}

export function getCases(queries) {
  return fetch(ES_URL, optionsGen("POST", esqCases(queries, true))).then(resp => resp.json());
}

export function getCaseFilings(caseNumber) {
  return fetch(
    ES_URL,
    optionsGen(
      "POST",
      toESQuery({
        case_number: caseNumber
      })
    )
  ).then(resp => resp.json());
}

export function getCaseCount(queries) {
  return fetch(ES_URL, optionsGen("POST", toESQuery(queries, [], 0)))
    .then(resp => resp.json())
    .then(json => json.hits.total.value);
}

export function getDistinctValues(field, val) {
  return fetch(
    ES_URL,
    optionsGen(
      "POST",
      toESGroupByQuery(field, {
        [field]: val
      })
    )
  )
    .then(resp => resp.json())
    .then(json => json.aggregations[field].buckets.map(e => toTitleCase(e.key)));
}

export function getTopParties(params, queries, k) {
  let field = params.type === "p" ? "petitioners" : "patent_owners";
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery(field, queries, k)))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[field].buckets.map(e => ({
        name: toTitleCase(e.key),
        count: e.doc_count
      }))
    );
}

export function getCasesByDate(queries) {
  return all([
    fetch(ES_URL, optionsGen("POST", toESDateHistogramQuery("filing_date", "month", queries))).then(resp => resp.json()),
    fetch(ES_URL, optionsGen("POST", toESDateHistogramQuery("up_termination_date", "month", queries))).then(resp => resp.json())
  ]).then(([resFD, resTD]) => {
    let bucketsFD = resFD.aggregations.filing_date.buckets;
    let bucketsTD = resTD.aggregations.up_termination_date.buckets;
    return [
      {
        k: "Filed",
        v: bucketsFD.map(e => ({
          k: e.key_as_string,
          v: e.doc_count
        }))
      },
      {
        k: "Terminated",
        v: bucketsTD.map(e => ({
          k: e.key_as_string,
          v: e.doc_count
        }))
      }
    ];
  });
}

export function getCasesByPhase(queries) {
  return all([
    fetch(
      ES_URL,
      optionsGen(
        "POST",
        toESDoubleGroupByQuery("up_status", "up_substatus", {
          ...queries,
          up_status: "Not Instituted"
        })
      )
    ).then(resp => resp.json()),
    fetch(ES_URL, optionsGen("POST", toESTripleGroupByQuery("up_institution_phase", "up_status_concat", "claim_outcome_at_fwd", queries))).then(resp => resp.json())
  ]).then(([jsonNI, json]) => {
    let res = [];
    // not instituted status
    if (!queries.up_status || queries.up_status === "Not Instituted") {
      jsonNI.aggregations.up_status.buckets.forEach(b => {
        let resNI = [];
        b.up_substatus.buckets.forEach(bb => resNI.push({ k: toTitleCase(bb.key), v: bb.doc_count }));
        res.push({
          k: toTitleCase(b.key),
          v: resNI
        });
      });
    }
    // other status with phases
    json.aggregations.up_institution_phase.buckets.forEach(b => {
      let resPhase = [];
      let sumPending = 0;
      b.up_status_concat.buckets.forEach(bb => {
        if (!bb.key.startsWith("not instituted")) {
          if (bb.key == "final written decision" || bb.key == "adverse judgment") {
            let fwd = [];
            let sumFwd = 0;
            bb.claim_outcome_at_fwd.buckets.forEach(bbb => {
              fwd.push({ k: toTitleCase(bbb.key), v: bbb.doc_count });
              sumFwd += bbb.doc_count;
            });
            fwd.push({ k: "N/A", v: bb.doc_count - sumFwd });
            resPhase.push({ k: toTitleCase(bb.key), v: fwd });
          } else if (bb.key.startsWith("pending")) {
            sumPending += bb.doc_count;
          } else {
            resPhase.push({ k: toTitleCase(bb.key), v: bb.doc_count });
          }
        }
      });
      resPhase.push({ k: "Pending", v: sumPending });
      res.push({ k: toTitleCase(b.key), v: resPhase });
    });
    // done
    return res;
  });
}

export function getCasesByPetitionerLawfirmInstitutionOutcome(queries, size = 25) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("lawfirms.petitioner", "up_institution_outcome", queries, size)))
    .then(resp => resp.json())
    .then(resP => {
      let p = {};
      resP.aggregations["lawfirms.petitioner"].buckets.forEach(b => {
        p[toTitleCase(b.key)] = {};
        b.up_institution_outcome.buckets.forEach(bib => (p[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return p;
    });
}

export function getCasesByPatentOwnerLawfirmInstitutionOutcome(queries, size = 25) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("lawfirms.patent_owner", "up_institution_outcome", queries, size)))
    .then(resp => resp.json())
    .then(resPO => {
      let po = {};
      resPO.aggregations["lawfirms.patent_owner"].buckets.forEach(b => {
        po[toTitleCase(b.key)] = {};
        b.up_institution_outcome.buckets.forEach(bib => (po[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return po;
    });
}

export function getCasesByAuthorJudgeInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("judges.author", "up_institution_outcome", omit(queries, ["size"]), queries.size)))
    .then(resp => resp.json())
    .then(resA => {
      let author = {};
      resA.aggregations["judges.author"].buckets.forEach(b => {
        author[toTitleCase(b.key)] = {};
        b.up_institution_outcome.buckets.forEach(bib => (author[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return author;
    });
}

export function getCasesByPanelJudgeInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("judges.panel", "up_institution_outcome", omit(queries, ["size"]), queries.size)))
    .then(resp => resp.json())
    .then(resP => {
      let panel = {};
      resP.aggregations["judges.panel"].buckets.forEach(b => {
        panel[toTitleCase(b.key)] = {};
        b.up_institution_outcome.buckets.forEach(bib => (panel[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return panel;
    });
}

export function getCasesByPetitionerLawfirmFWDOutcome(queries, size = 25) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("lawfirms.petitioner", "claim_outcome_at_fwd", queries, size)))
    .then(resp => resp.json())
    .then(resP => {
      let p = {};
      resP.aggregations["lawfirms.petitioner"].buckets.forEach(b => {
        p[toTitleCase(b.key)] = {};
        b.claim_outcome_at_fwd.buckets.forEach(bib => (p[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return p;
    });
}

export function getCasesByPatentOwnerLawfirmFWDOutcome(queries, size = 25) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("lawfirms.patent_owner", "claim_outcome_at_fwd", queries, size)))
    .then(resp => resp.json())
    .then(resPO => {
      let po = {};
      resPO.aggregations["lawfirms.patent_owner"].buckets.forEach(b => {
        po[toTitleCase(b.key)] = {};
        b.claim_outcome_at_fwd.buckets.forEach(bib => (po[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return po;
    });
}

export function getCasesByAuthorJudgeFWDOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("judges.author", "claim_outcome_at_fwd", omit(queries, ["size"]), queries.size)))
    .then(resp => resp.json())
    .then(resA => {
      let author = {};
      resA.aggregations["judges.author"].buckets.forEach(b => {
        author[toTitleCase(b.key)] = {};
        b.claim_outcome_at_fwd.buckets.forEach(bib => (author[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return author;
    });
}

export function getCasesByPanelJudgeFWDOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", toESDoubleGroupByQuery("judges.panel", "claim_outcome_at_fwd", omit(queries, ["size"]), queries.size)))
    .then(resp => resp.json())
    .then(resP => {
      let panel = {};
      resP.aggregations["judges.panel"].buckets.forEach(b => {
        panel[toTitleCase(b.key)] = {};
        b.claim_outcome_at_fwd.buckets.forEach(bib => (panel[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return panel;
    });
}

export function getCasesByTechCenter(queries) {
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery("ptab_tech_center", queries)))
    .then(resp => resp.json())
    .then(json => {
      let otherCount = json.aggregations.ptab_tech_center.sum_other_doc_count;
      let res = otherCount
        ? {
          Other: otherCount
        }
        : {};
      let keyCountPairs = json.aggregations.ptab_tech_center.buckets;
      keyCountPairs.forEach(e => (res[toTitleCase(e.key)] = e.doc_count));
      return res;
    });
}

export function getCasesByIndustry(queries) {
  const INDUSTRY_MAP = {
    T: "High-Tech (T)",
    M: "Medical (M)",
    O: "Other (O)"
  };
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery("up_industry", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations.up_industry.buckets;
      keyCountPairs.forEach(e => (res[INDUSTRY_MAP[toTitleCase(e.key)]] = e.doc_count));
      return res;
    });
}

export function getCasesByPOEntityType(queries) {
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery("up_po_type", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations.up_po_type.buckets;
      keyCountPairs.forEach(e => (res[e.key.toUpperCase()] = e.doc_count));
      return res;
    });
}

export function getCasesByPetEntityType(queries) {
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery("up_pet_type", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations.up_pet_type.buckets;
      keyCountPairs.forEach(e => (res[e.key.toUpperCase()] = e.doc_count));
      return res;
    });
}

export function getCasesByPOEntityTypeOverYears(queries) {
  return fetch(ES_URL, optionsGen("POST", toESDateHistogramGroupByQuery("filing_date", "up_po_type", "year", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations["filing_date"].buckets.forEach(b => {
        const bk = b.key_as_string.substring(0, 4);
        res[bk] = {};
        b.up_po_type.buckets.forEach(bib => (res[bk][bib.key.toUpperCase()] = bib.doc_count));
      });
      return res;
    });
}

export function getCasesByTypeOverYears(queries) {
  return fetch(ES_URL, optionsGen("POST", toESDateHistogramGroupByQuery("filing_date", "type", "year", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations["filing_date"].buckets.forEach(b => {
        const bk = b.key_as_string.substring(0, 4);
        res[bk] = {};
        b.type.buckets.forEach(bib => (res[bk][bib.key.toUpperCase()] = bib.doc_count));
      });
      return res;
    });
}

export function getCasesByInstitutionOutcomeOverYears(queries) {
  const dateField = "up_institution_date",
    groupbyField = "up_institution_outcome";
  return fetch(ES_URL, optionsGen("POST", toESDateHistogramGroupByQuery(dateField, groupbyField, "year", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations[dateField].buckets.forEach(b => {
        const bk = b.key_as_string.substring(0, 4);
        res[bk] = {};
        b[groupbyField].buckets.forEach(bib => (res[bk][toTitleCase(bib.key)] = bib.doc_count));
      });
      return res;
    });
}

export function getCasesByClaimInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery("claim_outcome_at_institution", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations["claim_outcome_at_institution"].buckets;
      keyCountPairs.forEach(e => (res[toTitleCase(e.key)] = e.doc_count));
      return res;
    });
}

export function getCasesByClaimDecisionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery("claim_outcome_at_fwd", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations["claim_outcome_at_fwd"].buckets;
      keyCountPairs.forEach(e => (res[toTitleCase(e.key)] = e.doc_count));
      return res;
    });
}

export function getClaimsByInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESGroupByQuery("claims.instituted_outcome", queries))))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations.claims["claims.instituted_outcome"].buckets;
      keyCountPairs.forEach(e => (res[toTitleCase(e.key)] = e.doc_count));
      return res;
    });
}

export function getClaimsByInstitutionOutcomeAndGround(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("claims.asserted_grounds", "claims.instituted_outcome", queries))))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations.claims["claims.asserted_grounds"].buckets.forEach(b => {
        let groundKey = toTitleCase(b.key).replace(",", " & ");
        res[groundKey] = {};
        b["claims.instituted_outcome"].buckets.forEach(bib => (res[groundKey][toTitleCase(bib.key)] = bib.doc_count));
      });
      return res;
    });
}

export function getClaimsByOutcomeAtInstitution(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESGroupByQuery("claims.fwd_outcome", queries))))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      let keyCountPairs = json.aggregations.claims["claims.fwd_outcome"].buckets;
      keyCountPairs.forEach(e => (res[toTitleCase(e.key)] = e.doc_count));
      return res;
    });
}

export function getClaimsByOutcomeAndGroundAtInstitution(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESTripleGroupByQuery("claims.asserted_grounds", "claims.instituted_grounds", "claims.fwd_outcome", queries))))
    .then(resp => resp.json())
    .then(json => {
      let res = {
        "102": {},
        "102 & ~103": {},
        "102 & 103": {},
        "~102 & 103": {},
        "103": {}
      };
      json.aggregations.claims["claims.asserted_grounds"].buckets.forEach(b => {
        b["claims.instituted_grounds"].buckets.forEach(bb => {
          bb["claims.fwd_outcome"].buckets.forEach(bbb => {
            if (b.key === "102" && bb.key === "102") {
              res["102"][toTitleCase(bbb.key)] = bbb.doc_count;
            } else if (b.key.indexOf("102") >= 0 && b.key.indexOf("103") >= 0) {
              if (bb.key === "102") {
                res["102 & ~103"][toTitleCase(bbb.key)] = bbb.doc_count;
              } else if (bb.key.indexOf("102") >= 0 && bb.key.indexOf("103") >= 0) {
                res["102 & 103"][toTitleCase(bbb.key)] = bbb.doc_count;
              } else if (bb.key === "103") {
                res["~102 & 103"][toTitleCase(bbb.key)] = bbb.doc_count;
              }
            } else if (b.key === "103" && bb.key === "103") {
              res["103"][toTitleCase(bbb.key)] = bbb.doc_count;
            }
          });
        });
      });
      return res;
    });
}

export function getClaimsByPetitionerLawfirmInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("lawfirms.petitioner", "claims.instituted_outcome", queries), "lawfirms.petitioner")))
    .then(resp => resp.json())
    .then(resP => {
      let p = {};
      resP.aggregations["lawfirms.petitioner"].buckets.forEach(b => {
        p[toTitleCase(b.key)] = {};
        b.claims["claims.instituted_outcome"].buckets.forEach(bib => (p[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return p;
    });
}

export function getClaimsByPatentOwnerLawfirmInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("lawfirms.patent_owner", "claims.instituted_outcome", queries), "lawfirms.patent_owner")))
    .then(resp => resp.json())
    .then(resPO => {
      let po = {};
      resPO.aggregations["lawfirms.patent_owner"].buckets.forEach(b => {
        po[toTitleCase(b.key)] = {};
        b.claims["claims.instituted_outcome"].buckets.forEach(bib => (po[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return po;
    });
}

export function getClaimsByAuthorJudgeInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("judges.author", "claims.instituted_outcome", queries), "judges.author")))
    .then(resp => resp.json())
    .then(resA => {
      let author = {};
      resA.aggregations["judges.author"].buckets.forEach(b => {
        author[toTitleCase(b.key)] = {};
        b.claims["claims.instituted_outcome"].buckets.forEach(bib => (author[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return author;
    });
}

export function getClaimsByPanelJudgeInstitutionOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("judges.panel", "claims.instituted_outcome", queries), "judges.panel")))
    .then(resp => resp.json())
    .then(resP => {
      let panel = {};
      resP.aggregations["judges.panel"].buckets.forEach(b => {
        panel[toTitleCase(b.key)] = {};
        b.claims["claims.instituted_outcome"].buckets.forEach(bib => (panel[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return panel;
    });
}

export function getClaimsByPetitionerLawfirmFWDOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("lawfirms.petitioner", "claims.fwd_outcome", queries), "lawfirms.petitioner")))
    .then(resp => resp.json())
    .then(resP => {
      let p = {};
      resP.aggregations["lawfirms.petitioner"].buckets.forEach(b => {
        p[toTitleCase(b.key)] = {};
        b.claims["claims.fwd_outcome"].buckets.forEach(bib => (p[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return p;
    });
}

export function getClaimsByPatentOwnerLawfirmFWDOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("lawfirms.patent_owner", "claims.fwd_outcome", queries), "lawfirms.patent_owner")))
    .then(resp => resp.json())
    .then(resPO => {
      let po = {};
      resPO.aggregations["lawfirms.patent_owner"].buckets.forEach(b => {
        po[toTitleCase(b.key)] = {};
        b.claims["claims.fwd_outcome"].buckets.forEach(bib => (po[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return po;
    });
}

export function getClaimsByAuthorJudgeFWDOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("judges.author", "claims.fwd_outcome", queries), "judges.author")))
    .then(resp => resp.json())
    .then(resA => {
      let author = {};
      resA.aggregations["judges.author"].buckets.forEach(b => {
        author[toTitleCase(b.key)] = {};
        b.claims["claims.fwd_outcome"].buckets.forEach(bib => (author[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return author;
    });
}

export function getClaimsByPanelJudgeFWDOutcome(queries) {
  return fetch(ES_URL, optionsGen("POST", nestedPathAggs("claims", toESDoubleGroupByQuery("judges.panel", "claims.fwd_outcome", queries), "judges.panel")))
    .then(resp => resp.json())
    .then(resP => {
      let panel = {};
      resP.aggregations["judges.panel"].buckets.forEach(b => {
        panel[toTitleCase(b.key)] = {};
        b.claims["claims.fwd_outcome"].buckets.forEach(bib => (panel[toTitleCase(b.key)][toTitleCase(bib.key)] = bib.doc_count));
      });
      return panel;
    });
}

export function getCasesByPetitionerLawfirmPerformance(queries) {
  const sizeVisible = queries.size;
  const sizeAPI = 2 * queries.size;
  const query = omit(queries, ["size"]);
  return all([getCasesByPetitionerLawfirmInstitutionOutcome(query, sizeAPI), getCasesByPetitionerLawfirmFWDOutcome(query, sizeAPI)]).then(([resInst, resFWD]) => {
    const lawfirms = intersection(Object.keys(resInst), Object.keys(resFWD));
    let scores = [];
    let res = {};
    lawfirms.forEach(l => {
      if (!res[l]) {
        res[l] = {};
      }
      const numInst = resInst[l]["Instituted - Granted"] || 0;
      const numNotInstM = resInst[l]["Not Instituted - Merits"] || 0;
      const numFWDP = resFWD[l]["All Claims Patentable"] || 0;
      scores.push([l, (numInst - numNotInstM - 2 * numFWDP) / (numInst + numNotInstM)]);
      res[l]["Instituted - Granted"] = numInst;
      res[l]["Not Instituted - Merits"] = numNotInstM;
      res[l]["All Claims Patentable"] = numFWDP;
    });
    return pick(
      res,
      sortBy(scores, s => -s[1])
        .slice(0, sizeVisible)
        .map(s => s[0])
    );
  });
}

export function getCasesByPatentOwnerLawfirmPerformance(queries) {
  const sizeVisible = queries.size;
  const sizeAPI = 2 * sizeVisible;
  const query = omit(queries, ["size"]);
  return all([getCasesByPatentOwnerLawfirmInstitutionOutcome(query, sizeAPI), getCasesByPatentOwnerLawfirmFWDOutcome(query, sizeAPI)]).then(([resInst, resFWD]) => {
    const lawfirms = intersection(Object.keys(resInst), Object.keys(resFWD));
    let scores = [];
    let res = {};
    lawfirms.forEach(l => {
      if (!res[l]) {
        res[l] = {};
      }
      const numInst = resInst[l]["Instituted - Granted"] || 0;
      const numNotInstM = resInst[l]["Not Instituted - Merits"] || 0;
      const numFWDP = resFWD[l]["All Claims Patentable"] || 0;
      scores.push([l, (numInst - numNotInstM - 2 * numFWDP) / (numInst + numNotInstM)]);
      res[l]["Instituted - Granted"] = numInst;
      res[l]["Not Instituted - Merits"] = numNotInstM;
      res[l]["All Claims Patentable"] = numFWDP;
    });
    return pick(
      res,
      sortBy(scores, s => s[1])
        .slice(0, sizeVisible)
        .map(s => s[0])
    );
  });
}
